var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { createSlice } from '@reduxjs/toolkit';
import createCase from '../../redux/cases/createCase';
import { FIRST_QUESTION_ID, RECURRING_TABLE_LIST_QUESTION_ID, REDISPUTE_FIRST_QUESTION_ID } from '../../const/questionIds';
import { formatCurrency, formatDisplayDateWithFormat } from 'syf-js-utilities';
import getQuestionById from '../../helpers/getQuestionById';
import triggerQualtrics from '../../helpers/triggerQualtrics';
import getRedisputeEligibility from '../../redux/transactionEligible/getRedisputeEligibility';
import fetchQuestionnaire from './fetchQuestionnaire';
import { ErrorSource, QuestionnaireFlow } from './typings';
export var initialState = {
    step: QuestionnaireFlow.Start,
    previousStep: QuestionnaireFlow.Start,
    questions: [],
    previousQuestions: [],
    currentQuestion: null,
    questionnaireError: null,
    businessError: { source: '' },
    transactionAccountCredits: [],
    isRedispute: false,
    duplicateTransactions: [],
    doubleChargedTransactions: []
};
var convertTransactionDetailsToAccountCredit = function (status) {
    return function (_a) {
        var amount = _a.amount, date = _a.date, transactionDescription = _a.transactionDescription;
        var parsedCreditAmount = Math.abs(parseFloat(amount));
        return {
            creditIssuedDate: formatDisplayDateWithFormat(date, 'MMM d'),
            description: transactionDescription,
            creditAmount: "+".concat(formatCurrency(parsedCreditAmount.toString())),
            status: status
        };
    };
};
var convertRecurringTransactionToDuplicateTransaction = function (_a) {
    var amount = _a.amount, date = _a.date, id = _a.id, merchantAddress = _a.merchantAddress, merchantName = _a.merchantName, referenceNumber = _a.referenceNumber, statementDate = _a.statementDate, transactionDescription = _a.transactionDescription;
    var transactionAmount = Math.abs(parseFloat(amount)).toFixed(2);
    return {
        address: merchantAddress,
        description: transactionDescription,
        formattedDate: formatDisplayDateWithFormat(date, 'MMM d'),
        purchaseLocation: merchantName,
        referenceNumber: referenceNumber,
        selected: false,
        statementDate: statementDate,
        transactionAmount: transactionAmount,
        transactionDate: date,
        transactionId: id
    };
};
var questionnaireSlice = createSlice({
    name: 'questionnaire',
    initialState: initialState,
    reducers: {
        nextQuestionUpdated: function (state, action) {
            state.previousQuestions = __spreadArray(__spreadArray([], state.previousQuestions, true), [
                state.currentQuestion
            ], false);
            state.currentQuestion = action.payload;
        },
        previousQuestionUpdated: function (state) {
            var previousQuestions = state.previousQuestions, currentQuestion = state.currentQuestion;
            if ((currentQuestion === null || currentQuestion === void 0 ? void 0 : currentQuestion.id) === RECURRING_TABLE_LIST_QUESTION_ID) {
                state.duplicateTransactions = state.duplicateTransactions.map(function (txn) { return (__assign(__assign({}, txn), { selected: false })); });
            }
            state.currentQuestion = previousQuestions[previousQuestions.length - 1];
            state.previousQuestions = previousQuestions.slice(0, previousQuestions.length - 1);
        },
        stepUpdated: function (state, action) {
            var _a;
            var questions = state.questions, currentStep = state.step, isRedispute = state.isRedispute;
            var nextStep = action.payload;
            var previousQuestions = nextStep === QuestionnaireFlow.ContactFraud &&
                ((_a = state.currentQuestion) === null || _a === void 0 ? void 0 : _a.id) === 'Recognize Charge'
                ? __spreadArray(__spreadArray([], state.previousQuestions, true), [state.currentQuestion], false) : __spreadArray([], state.previousQuestions, true);
            if (currentStep === QuestionnaireFlow.CreditDetails) {
                var firstQuestion = getQuestionById({
                    questionnaire: questions,
                    id: isRedispute ? REDISPUTE_FIRST_QUESTION_ID : FIRST_QUESTION_ID
                });
                if (!firstQuestion) {
                    throw new Error("Could not find question ".concat(FIRST_QUESTION_ID, " in questionnaire"));
                }
                state.currentQuestion = firstQuestion;
            }
            state.step = nextStep;
            state.previousQuestions = previousQuestions;
        },
        previousStepUpdated: function (state, action) {
            state.previousStep = action.payload;
        },
        businessErrorUpdated: function (state, action) {
            state.businessError = { source: action.payload };
        },
        updateRedispute: function (state, action) {
            state.isRedispute = action.payload;
        },
        toggleDuplicateTransaction: function (state, action) {
            var selectedTransactionId = action.payload.transactionId;
            state.duplicateTransactions.forEach(function (txn) {
                if (txn.transactionId === selectedTransactionId) {
                    txn.selected = !txn.selected;
                }
            });
        },
        questionnaireCleared: function () {
            return initialState;
        }
    },
    extraReducers: function (builder) {
        builder.addCase(fetchQuestionnaire.fulfilled, function (state, action) {
            var _a;
            var _b = action.payload, questionnaire = _b.questionnaire, creditIssuedTransactions = _b.creditIssuedTransactions, recurringTransactions = _b.recurringTransactions, doubleChargedTransactions = _b.doubleChargedTransactions;
            var pendingTransactions = creditIssuedTransactions.pendingTransactions, regularTransactions = creditIssuedTransactions.regularTransactions;
            var firstQuestion = questionnaire.find(function (question) { return question.id === FIRST_QUESTION_ID; });
            if (!firstQuestion) {
                throw new Error("Could not find question ".concat(FIRST_QUESTION_ID, " in questionnaire"));
            }
            state.questions = questionnaire;
            state.currentQuestion = firstQuestion;
            (_a = state.transactionAccountCredits).push.apply(_a, __spreadArray(__spreadArray([], pendingTransactions.map(convertTransactionDetailsToAccountCredit('PENDING')), false), regularTransactions.map(convertTransactionDetailsToAccountCredit('POSTED')), false));
            if (recurringTransactions) {
                state.duplicateTransactions = recurringTransactions.map(convertRecurringTransactionToDuplicateTransaction);
            }
            if (doubleChargedTransactions) {
                state.doubleChargedTransactions = doubleChargedTransactions.map(convertRecurringTransactionToDuplicateTransaction);
            }
            state.step = state.transactionAccountCredits.length
                ? QuestionnaireFlow.CreditDetails
                : QuestionnaireFlow.InProgress;
        });
        builder.addCase(fetchQuestionnaire.rejected, function (state) {
            state.step = QuestionnaireFlow.BusinessError;
            state.businessError = { source: ErrorSource.Questionnaire };
        });
        builder.addCase(createCase.fulfilled, function (state) {
            state.step = QuestionnaireFlow.End;
            triggerQualtrics(QuestionnaireFlow.End);
        });
        builder.addCase(createCase.rejected, function (state, action) {
            // eslint-disable-next-line no-console
            console.error(action.payload);
            state.step = QuestionnaireFlow.BusinessError;
            state.businessError = { source: ErrorSource.PostCases };
        });
        builder.addCase(getRedisputeEligibility.fulfilled, function (state, action) {
            var _a;
            var eligible = action.payload.transactionEligible.reDisputeEligible;
            if (!eligible) {
                state.step = QuestionnaireFlow.RedisputeNotEligible;
                return;
            }
            if (!action.payload.questionnaire) {
                state.step = QuestionnaireFlow.APIError;
                return;
            }
            var _b = action.payload.questionnaire, questionnaire = _b.questionnaire, creditIssuedTransactions = _b.creditIssuedTransactions, recurringTransactions = _b.recurringTransactions, reDisputeEligible = _b.reDisputeEligible, originalCaseSummary = _b.originalCaseSummary;
            var pendingTransactions = creditIssuedTransactions.pendingTransactions, regularTransactions = creditIssuedTransactions.regularTransactions;
            if (!reDisputeEligible) {
                state.step = QuestionnaireFlow.RedisputeNotEligible;
                return;
            }
            var firstQuestion = questionnaire.find(function (question) { return question.id === REDISPUTE_FIRST_QUESTION_ID; });
            if (!firstQuestion) {
                throw new Error("Could not find question ".concat(REDISPUTE_FIRST_QUESTION_ID, " in questionnaire"));
            }
            state.questions = questionnaire;
            state.currentQuestion = firstQuestion;
            (_a = state.transactionAccountCredits).push.apply(_a, __spreadArray(__spreadArray([], pendingTransactions.map(convertTransactionDetailsToAccountCredit('PENDING')), false), regularTransactions.map(convertTransactionDetailsToAccountCredit('POSTED')), false));
            if (recurringTransactions) {
                state.duplicateTransactions = recurringTransactions.map(convertRecurringTransactionToDuplicateTransaction);
            }
            state.step = state.transactionAccountCredits.length
                ? QuestionnaireFlow.CreditDetails
                : QuestionnaireFlow.InProgress;
            state.originalCaseSummary = originalCaseSummary;
        });
        builder.addCase(getRedisputeEligibility.rejected, function (state) {
            state.step = QuestionnaireFlow.APIError;
        });
    }
});
export var businessErrorUpdated = (_a = questionnaireSlice.actions, _a.businessErrorUpdated), nextQuestionUpdated = _a.nextQuestionUpdated, previousQuestionUpdated = _a.previousQuestionUpdated, previousStepUpdated = _a.previousStepUpdated, questionnaireCleared = _a.questionnaireCleared, stepUpdated = _a.stepUpdated, toggleDuplicateTransaction = _a.toggleDuplicateTransaction, updateRedispute = _a.updateRedispute;
export default questionnaireSlice.reducer;
//# sourceMappingURL=questionnaire.slice.js.map